/**
 * AiScribeRecorder — ambient AI medical scribe widget.
 *
 * Flow:
 *  1. Click "Start scribing" → consent modal (HIPAA + GDPR) if not yet granted.
 *  2. Once consent recorded, MediaRecorder captures mic audio.
 *  3. Click "Stop" → post-recording modal asks: "Keep audio" vs "Transcribe & delete".
 *  4. Audio uploaded to POST /api/ai/scribe → returns transcript + structured SOAP/DAP draft.
 *  5. Draft inserted into the parent form via `onDraft` callback.
 *
 * Privacy: audio is deleted on the server unless the user explicitly opts to
 * keep it. The consent record is upserted to /api/ai/scribe/consent.
 */
import React, { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { Mic, MicOff, Loader2, ShieldCheck, AlertTriangle, X, Trash2, Archive } from 'lucide-react';
import { toast } from 'sonner';

export default function AiScribeRecorder({
  patientId,
  appointmentId = null,
  format = 'SOAP',
  discipline = 'physiotherapy',
  onDraft,                 // (draft) => void  — called when transcription completes
  disabled = false,
}) {
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentGranted, setConsentGranted] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [showPostRecord, setShowPostRecord] = useState(false);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tickRef = useRef(null);

  // Pre-fetch consent state for this patient.
  useEffect(() => {
    if (!patientId) return;
    api.get(`/ai/scribe/consent/${patientId}`)
      .then(r => {
        const granted = r.data && r.data.granted_at && !r.data.revoked;
        setConsentGranted(!!granted);
        setConsentChecked(true);
      })
      .catch(() => setConsentChecked(true));
  }, [patientId]);

  const startTick = () => {
    setElapsed(0);
    tickRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
  };
  const stopTick = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  };

  const ensureConsent = async () => {
    if (consentGranted) return true;
    setShowConsent(true);
    return false;
  };

  const acceptConsent = async () => {
    try {
      await api.post('/ai/scribe/consent', { patient_id: patientId, locale: 'en' });
      setConsentGranted(true);
      setShowConsent(false);
      toast.success('Consent recorded.');
      // Auto-start recording after granting consent.
      setTimeout(() => startRecording(), 100);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record consent.');
    }
  };

  const startRecording = async () => {
    if (!await ensureConsent()) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      // Pick the best supported MIME — Safari needs mp4, Chrome/FF webm.
      const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
      const mimeType = candidates.find(m => MediaRecorder.isTypeSupported(m)) || '';
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = rec;
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        setAudioBlob(blob);
        // Always stop tracks ASAP — leaving the mic open is a privacy red flag.
        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        stopTick();
        setShowPostRecord(true);
      };
      rec.start();
      setRecording(true);
      startTick();
    } catch (e) {
      toast.error(e?.message || 'Microphone access denied.');
    }
  };

  const stopRecording = () => {
    if (!recording) return;
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const submit = async (keepAudio) => {
    if (!audioBlob) return;
    setShowPostRecord(false);
    setProcessing(true);
    const fd = new FormData();
    fd.append('audio', audioBlob, `scribe-${Date.now()}.webm`);
    fd.append('patient_id', patientId);
    fd.append('fmt', format);
    fd.append('discipline', discipline);
    fd.append('keep_audio', keepAudio ? 'true' : 'false');
    if (appointmentId) fd.append('appointment_id', appointmentId);
    try {
      const r = await api.post('/ai/scribe', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      onDraft?.(r.data);
      toast.success(
        keepAudio
          ? `Transcribed (${r.data.transcript.length} chars). Audio kept in records.`
          : `Transcribed (${r.data.transcript.length} chars). Audio deleted.`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scribe failed.');
    } finally {
      setProcessing(false);
      setAudioBlob(null);
    }
  };

  const fmtMMSS = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  if (!consentChecked) return null;

  return (
    <>
      <div className="flex items-center gap-2" data-testid="ai-scribe-widget">
        {!recording && !processing && (
          <button
            type="button"
            onClick={startRecording}
            disabled={disabled}
            className="btn-secondary inline-flex items-center gap-2 text-sm"
            data-testid="scribe-start"
          >
            <Mic className="w-4 h-4 text-[#B83A3A]"/> AI scribe
          </button>
        )}
        {recording && (
          <div className="inline-flex items-center gap-3 px-3 py-1.5 rounded-md border border-[#B83A3A] bg-[#B83A3A]/10" data-testid="scribe-recording">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#B83A3A] opacity-75"/>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#B83A3A]"/>
            </span>
            <span className="font-mono text-sm text-[#B83A3A]">{fmtMMSS(elapsed)}</span>
            <button onClick={stopRecording} className="inline-flex items-center gap-1 text-sm text-[#B83A3A] font-medium" data-testid="scribe-stop">
              <MicOff className="w-4 h-4"/> Stop
            </button>
          </div>
        )}
        {processing && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-brand bg-brand/10 text-sm text-brand">
            <Loader2 className="w-4 h-4 animate-spin"/> Transcribing…
          </div>
        )}
      </div>

      {/* ──────────────── Consent modal ──────────────── */}
      {showConsent && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="scribe-consent-modal">
          <div className="bg-bg-primary rounded-lg max-w-2xl w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start gap-3 mb-4">
              <ShieldCheck className="w-6 h-6 text-brand shrink-0 mt-0.5"/>
              <div>
                <h3 className="text-xl font-heading font-bold mb-1">AI ambient scribe — consent</h3>
                <p className="text-sm text-fg-muted">HIPAA + GDPR notice. Please review with your patient and confirm verbal or written consent before recording.</p>
              </div>
            </div>

            <div className="space-y-3 text-sm text-fg-secondary mb-5">
              <p>
                <strong className="text-fg">What this does.</strong> Our AI scribe records the audio of your
                consultation, transcribes it using OpenAI Whisper, and produces a draft clinical note (SOAP or DAP)
                in the discipline-specific style of your clinic. You always review and edit before saving.
              </p>
              <p>
                <strong className="text-fg">Audio handling.</strong> By default the audio is <strong>deleted</strong> from
                our servers within seconds of transcription. You may opt to keep the audio in the patient's
                medical-document file — clinicians choose this only when needed for legal protection. When kept,
                the audio is encrypted at rest and access is logged.
              </p>
              <p>
                <strong className="text-fg">Where the data goes.</strong> Audio is uploaded to OpenAI Whisper for
                transcription only; OpenAI does not retain or train on this data per their healthcare data
                processing agreement. Structured notes are produced by GPT-5.2 with the same retention guarantee.
                All processing complies with HIPAA (US), GDPR (EU) and Polish patient-record laws.
              </p>
              <p>
                <strong className="text-fg">Your patient's rights.</strong> Consent may be withdrawn at any time
                from the patient's records page — future scribing sessions will then be refused. Stored audio is
                deleted on withdrawal. The transcript and structured note become part of the medical record and
                are retained per Polish/EU patient-record law (typically 20 years).
              </p>
              <p>
                <strong className="text-fg">Liability.</strong> The AI-generated draft is a <em>starting point</em>;
                the clinician remains fully responsible for the final clinical record. Never save an unreviewed
                AI draft.
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 pt-4 border-t border-soft">
              <button onClick={()=>setShowConsent(false)} className="btn-secondary text-sm" data-testid="scribe-consent-decline">
                <X className="w-4 h-4 inline mr-1"/> Decline
              </button>
              <button onClick={acceptConsent} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="scribe-consent-accept">
                <ShieldCheck className="w-4 h-4"/> Patient consents — proceed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ──────────────── Post-recording modal ──────────────── */}
      {showPostRecord && audioBlob && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="scribe-post-record-modal">
          <div className="bg-bg-primary rounded-lg max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-[#FFB000] shrink-0 mt-0.5"/>
              <div>
                <h3 className="text-lg font-heading font-bold mb-1">Audio recorded — choose</h3>
                <p className="text-xs text-fg-muted">Duration: {fmtMMSS(elapsed)} · {(audioBlob.size / 1024).toFixed(0)} KB</p>
              </div>
            </div>

            <div className="space-y-2 mb-5">
              <button onClick={()=>submit(false)} className="w-full text-left p-3 border border-soft rounded-lg hover:border-brand transition group" data-testid="scribe-transcribe-delete">
                <div className="flex items-center gap-2">
                  <Trash2 className="w-4 h-4 text-[#B83A3A]"/>
                  <strong className="text-sm">Transcribe & delete audio</strong>
                  <span className="ml-auto text-[10px] text-fg-muted uppercase tracking-wider">Recommended</span>
                </div>
                <p className="text-xs text-fg-muted mt-1 ml-6">Audio is deleted from our servers immediately after transcription. Only the text note is kept.</p>
              </button>

              <button onClick={()=>submit(true)} className="w-full text-left p-3 border border-soft rounded-lg hover:border-brand transition group" data-testid="scribe-keep-audio">
                <div className="flex items-center gap-2">
                  <Archive className="w-4 h-4 text-brand"/>
                  <strong className="text-sm">Transcribe & keep audio in records</strong>
                </div>
                <p className="text-xs text-fg-muted mt-1 ml-6">Audio is stored as an encrypted medical document. Use only when legally required.</p>
              </button>
            </div>

            <div className="flex justify-end pt-3 border-t border-soft">
              <button onClick={()=>{ setShowPostRecord(false); setAudioBlob(null); }} className="text-sm text-fg-muted hover:text-fg" data-testid="scribe-discard">
                Discard recording
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
